import { makeAutoObservable, runInAction } from 'mobx';
import { productsApi } from '@/api/products';
import { RequestSequencer, isAbortError } from '@/api/requests';
import { normalizeApiError, type ApiError } from '@/lib/errors';
import type { Category, Product } from '@/types/products';

/**
 * ProductStore (V1-04) — catalog server state for the register, following the
 * MobX server-state discipline (01-architecture.md §4): scope-keyed data,
 * idle/loading/success/refreshing/error with retained last-good data,
 * RequestSequencer stale guards, explicit invalidation, reset() on scope
 * change.
 *
 * Catalog strategy (03-pos-shell.md P1):
 * - A cheap probe (`per_page=1`) measures the active menu-product count.
 * - ≤ MENU_PAGINATION_THRESHOLD → ONE `groupBy=category` load; category/Top
 *   filtering is client-side over that payload.
 * - Above the threshold → paged mode: chips come from GET /categories and
 *   browsing/search use server pagination. DOM virtualization alone is not a
 *   data strategy for 1,000-product menus.
 * - A non-empty search term ALWAYS uses server search (name/SKU LIKE), in
 *   both modes, so behavior does not fork on catalog size.
 *
 * Hard rule: the broken `featured` query param is never sent — "Top" is a
 * client-side view over `is_featured` (see src/api/products.ts).
 *
 * Wiring (lead integration):
 * - construct with `getScopeKey` returning an org/branch discriminator, e.g.
 *   `() => `${auth.organization?.id ?? 'none'}:${auth.branch?.id ?? 'none'}``
 * - register `auth.registerScopeListener(() => products.reset())`; the store
 *   ALSO self-heals by comparing the scope key before every load.
 */

export type ResourceStatus = 'idle' | 'loading' | 'success' | 'refreshing' | 'error';

export type CollectionFilter = `collection:${number}`;
export type CategoryFilter = number | 'top' | CollectionFilter | null;
export type MenuGrouping = 'category' | 'collection';

/** One heading-plus-cards block of the sectioned register grid. */
export interface MenuSection {
  /** Stable DOM/react key; also the scroll-spy identity. */
  key: string;
  /** The chip this section belongs to, in `categoryFilter` terms. */
  filter: CategoryFilter;
  name: string;
  products: Product[];
}

export interface ProductStoreOptions {
  /** Discriminates org/branch scope; data never survives a key change. */
  getScopeKey?: () => string;
  /** Menu-product count above which browsing switches to server pagination. */
  menuPaginationThreshold?: number;
  /**
   * Whether the grid leads with a "Top" shelf of featured products.
   *
   * ON when absent, because that is what this store has always done and a
   * bare `new ProductStore()` in a test must not silently lose a section.
   *
   * A RETAIL counter turns it off. The shelf is a genuine duplicate by design
   * — a featured product renders under Top AND under its own category, which
   * is how a printed menu's "popular" shelf behaves and why the tiles are keyed
   * `section:id`. In a restaurant that reads as a shortcut; in a shop, where
   * the operator is scanning a catalogue of distinct SKUs, it reads as the same
   * product listed twice and makes the category counts look wrong. Owner
   * decision: a shop shows each product exactly once.
   *
   * A thunk, like `getScopeKey`: the organization can change under a live
   * store, and a value captured at construction would keep a shop's grid on
   * whatever the last tenant was.
   */
  showFeaturedShelf?: () => boolean;
  /** Ecommerce browses manual collections; restaurants browse menu categories. */
  getMenuGrouping?: () => MenuGrouping;
}

const DEFAULT_THRESHOLD = 400;
const PAGE_SIZE = 50;
/** Addon pool cap in paged mode (one request; >100 addons truncate — documented). */
const ADDON_POOL_SIZE = 100;

export class ProductStore {
  /** 'menu' = grouped payload, client-side filtering; 'paged' = server pagination. */
  mode: 'menu' | 'paged' = 'menu';
  /** Active menu-product total measured by the probe (null before first load). */
  catalogTotal: number | null = null;

  categories: Category[] = [];
  /** Flattened active non-addon products (menu mode grid source). */
  menuProducts: Product[] = [];
  /** Addon pool used to resolve addons_config product ids. */
  addons: Product[] = [];
  lastSync: string | null = null;

  status: ResourceStatus = 'idle';
  error: ApiError | null = null;

  searchTerm = '';
  categoryFilter: CategoryFilter = null;

  /** Server-mode rows (search results or paged category browse). */
  pagedProducts: Product[] = [];
  pagedPage = 1;
  pagedLastPage = 1;
  pagedTotal = 0;
  pagedStatus: ResourceStatus = 'idle';
  pagedError: ApiError | null = null;

  private seq = new RequestSequencer();
  private options: ProductStoreOptions;
  private loadedScopeKey: string | null = null;
  /**
   * Products fetched one at a time by id (order-editor seeding), including the
   * nulls — a product that is genuinely gone must not be re-requested on every
   * keystroke that re-renders the line it belonged to.
   */
  private fetchedById = new Map<number, Product | null>();

  constructor(options: ProductStoreOptions = {}) {
    this.options = options;
    makeAutoObservable<ProductStore, 'seq' | 'options' | 'loadedScopeKey' | 'fetchedById'>(this, {
      seq: false,
      options: false,
      loadedScopeKey: false,
      fetchedById: false,
    });
  }

  // ── computeds ────────────────────────────────────────────────────────────

  get scopeKey(): string {
    return this.options.getScopeKey?.() ?? 'default';
  }

  private get threshold(): number {
    return this.options.menuPaginationThreshold ?? DEFAULT_THRESHOLD;
  }

  /** True while a search term drives the grid (server search in any mode). */
  get searching(): boolean {
    return this.searchTerm.trim().length > 0;
  }

  /** Whether the grid currently renders server-paged rows. */
  get usingPagedResults(): boolean {
    return this.searching || this.mode === 'paged';
  }

  /**
   * Client-derived "Top" — is_featured only, never the broken query param.
   *
   * Empty when the featured shelf is off (see `showFeaturedShelf`). The gate
   * lives HERE rather than only where the section is built, because the shelf
   * is reachable two ways — as a section in the grid and as a selected
   * `categoryFilter` — and gating one of them would leave the other as a way
   * back into the duplicated view the option exists to remove.
   */
  get topProducts(): Product[] {
    if (this.options.showFeaturedShelf?.() === false) return [];
    const source = this.usingPagedResults ? this.pagedProducts : this.menuProducts;
    return source.filter((p) => p.is_featured);
  }

  get menuGrouping(): MenuGrouping {
    return this.options.getMenuGrouping?.() ?? 'category';
  }

  /** Collection chips available in the currently loaded catalog/page. */
  get collectionFilters(): { id: number; name: string; count: number }[] {
    const seen = new Map<number, string>();
    const counts = new Map<number, number>();
    const source = this.usingPagedResults ? this.pagedProducts : this.menuProducts;
    for (const product of source) {
      for (const collection of product.manualCollections) {
        if (!seen.has(collection.id)) seen.set(collection.id, collection.name);
        counts.set(collection.id, (counts.get(collection.id) ?? 0) + 1);
      }
    }
    return [...seen].map(([id, name]) => ({ id, name, count: counts.get(id) ?? 0 }));
  }

  /** Rows the register grid shows for the current search/filter state. */
  get gridProducts(): Product[] {
    if (this.usingPagedResults) {
      if (this.categoryFilter === 'top') return this.topProducts;
      if (
        typeof this.categoryFilter === 'string' &&
        this.categoryFilter.startsWith('collection:')
      ) {
        const id = Number(this.categoryFilter.slice('collection:'.length));
        return this.pagedProducts.filter((p) =>
          p.manualCollections.some((collection) => collection.id === id),
        );
      }
      return this.pagedProducts;
    }
    if (this.categoryFilter === 'top') return this.topProducts;
    if (typeof this.categoryFilter === 'string' && this.categoryFilter.startsWith('collection:')) {
      const id = Number(this.categoryFilter.slice('collection:'.length));
      return this.menuProducts.filter((p) =>
        p.manualCollections.some((collection) => collection.id === id),
      );
    }
    if (typeof this.categoryFilter === 'number') {
      const id = this.categoryFilter;
      return this.menuProducts.filter((p) => p.category_id === id);
    }
    return this.menuProducts;
  }

  /**
   * Whether the grid can be browsed as CATEGORY SECTIONS rather than one flat
   * wall of cards.
   *
   * Only menu mode qualifies, and only when nothing is being searched. Paged
   * mode holds one server page at a time — the categories below the fold are
   * not in memory, so a sectioned view would advertise anchors that scroll to
   * nothing. A search result is a single ranked list by definition and has no
   * sections to spy on.
   */
  get sectioned(): boolean {
    // The ecommerce All-products view is the complete catalog, including SKUs
    // that have not been assigned to a collection yet. Keep that view flat;
    // otherwise the unassigned tail becomes a misleading "Uncategorized"
    // storefront category.
    if (!this.usingPagedResults && this.menuGrouping === 'collection') {
      return false;
    }
    return !this.usingPagedResults && this.menuSections.length > 1;
  }

  /**
   * The grid as an ordered list of sections, in menu order.
   *
   * "Top" leads as a real section rather than a filter, so every chip in the
   * strip means the same thing — scroll me there — instead of one chip
   * replacing the grid while its neighbours move within it. A featured product
   * therefore appears twice, in Top and again under its own category. That is
   * deliberate and is how a printed menu with a "popular" shelf behaves; the
   * cart is keyed by product id, so tapping either copy is the same product.
   *
   * Empty categories are dropped: a chip that scrolls to a heading with no
   * cards under it is worse than no chip.
   */
  get menuSections(): MenuSection[] {
    if (this.usingPagedResults) return [];
    const sections: MenuSection[] = [];

    // Off in retail — see `showFeaturedShelf`. Skipping the SECTION is what
    // removes the duplicate; the products themselves still appear, once, under
    // their own category, so nothing becomes unreachable and every category
    // count still matches the cards beneath it.
    const top = this.topProducts;
    if (top.length > 0) sections.push({ key: 'top', filter: 'top', name: 'Top', products: top });

    if (this.menuGrouping === 'collection') {
      const byCollection = new Map<number, { name: string; products: Product[] }>();
      for (const product of this.menuProducts) {
        if (product.manualCollections.length === 0) {
          continue;
        }
        const seen = new Set<number>();
        for (const collection of product.manualCollections) {
          if (seen.has(collection.id)) continue;
          seen.add(collection.id);
          const bucket = byCollection.get(collection.id);
          if (bucket) bucket.products.push(product);
          else byCollection.set(collection.id, { name: collection.name, products: [product] });
        }
      }
      for (const [id, group] of byCollection) {
        sections.push({
          key: `collection-${id}`,
          filter: `collection:${id}`,
          name: group.name,
          products: group.products,
        });
      }
      return sections;
    }

    const byCategory = new Map<number, Product[]>();
    const uncategorized: Product[] = [];
    for (const product of this.menuProducts) {
      if (product.category_id == null) uncategorized.push(product);
      else {
        const bucket = byCategory.get(product.category_id);
        if (bucket) bucket.push(product);
        else byCategory.set(product.category_id, [product]);
      }
    }

    for (const category of this.categories) {
      const products = byCategory.get(category.id);
      if (products?.length) {
        sections.push({
          key: `cat-${category.id}`,
          filter: category.id,
          name: category.name,
          products,
        });
        byCategory.delete(category.id);
      }
    }

    // Anything whose category row did not come back with the menu still has to
    // be sellable — it lands at the end rather than vanishing off the grid.
    const stranded = [...byCategory.values()].flat().concat(uncategorized);
    if (stranded.length > 0) {
      sections.push({ key: 'other', filter: null, name: 'Other', products: stranded });
    }
    return sections;
  }

  /** Status backing the grid (menu load vs server page). */
  get gridStatus(): ResourceStatus {
    return this.usingPagedResults ? this.pagedStatus : this.status;
  }

  get gridError(): ApiError | null {
    return this.usingPagedResults ? this.pagedError : this.error;
  }

  get canLoadMore(): boolean {
    return this.usingPagedResults && this.pagedPage < this.pagedLastPage;
  }

  // ── catalog loading ──────────────────────────────────────────────────────

  /** Boot/refresh entry. Probes size, then loads menu or paged world. */
  async loadCatalog(): Promise<void> {
    this.ensureScope();
    const ticket = this.seq.begin(`catalog:${this.scopeKey}`);
    const hadData = this.status === 'success' || this.menuProducts.length > 0;
    this.status = hadData ? 'refreshing' : 'loading';
    this.error = null;
    try {
      // Probe the active menu-product count (1-row page, cheap).
      const probe = await productsApi.list(
        { status: 'active', is_addon: false, per_page: 1 },
        { signal: ticket.signal, salesChannel: 'pos' },
      );
      if (!ticket.isCurrent()) return;

      if (probe.total <= this.threshold) {
        const menu = await productsApi.groupedMenu({ signal: ticket.signal, salesChannel: 'pos' });
        if (!ticket.isCurrent()) return;
        runInAction(() => {
          this.mode = 'menu';
          this.catalogTotal = probe.total;
          this.categories = menu.categories;
          this.menuProducts = menu.categories.flatMap((c) =>
            c.products.filter((p) => p.status === 'active' && !p.archived_at),
          );
          this.addons = menu.addons;
          this.lastSync = menu.lastSync;
          this.status = 'success';
        });
        return;
      }

      // Large catalog: categories for chips + addon pool, then first page.
      const [categoriesPage, addonsPage] = await Promise.all([
        productsApi.categories(
          { status: 'active', per_page: 100, sort_by: 'sort_order', sort_order: 'asc' },
          { signal: ticket.signal, salesChannel: 'pos' },
        ),
        productsApi.list(
          { status: 'active', is_addon: true, per_page: ADDON_POOL_SIZE },
          { signal: ticket.signal, salesChannel: 'pos' },
        ),
      ]);
      if (!ticket.isCurrent()) return;
      runInAction(() => {
        this.mode = 'paged';
        this.catalogTotal = probe.total;
        this.categories = categoriesPage.categories;
        this.menuProducts = [];
        this.addons = addonsPage.products;
        this.lastSync = addonsPage.lastSync;
        this.status = 'success';
      });
      await this.loadPage(1);
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent()) return;
      runInAction(() => {
        this.status = 'error';
        this.error = normalizeApiError(e);
      });
    }
  }

  /** Explicit invalidation — reloads the catalog keeping last-good data visible. */
  async invalidate(): Promise<void> {
    await this.loadCatalog();
  }

  // ── search / paged browsing ──────────────────────────────────────────────

  /**
   * Set the raw search term (debounce lives at the input). An empty term
   * returns to menu/category browsing; a non-empty term triggers server
   * search from page 1.
   */
  setSearchTerm(term: string): void {
    this.searchTerm = term;
    if (term.trim().length === 0) {
      this.pagedError = null;
      if (this.mode === 'paged') void this.loadPage(1);
      return;
    }
    void this.loadPage(1);
  }

  setCategoryFilter(filter: CategoryFilter): void {
    this.categoryFilter = filter;
    // 'top' is always a client-side view; server browse reloads for real categories.
    if (this.usingPagedResults && typeof filter === 'number') void this.loadPage(1);
  }

  async loadMore(): Promise<void> {
    if (!this.canLoadMore || this.pagedStatus === 'loading') return;
    await this.loadPage(this.pagedPage + 1, { append: true });
  }

  private async loadPage(page: number, opts: { append?: boolean } = {}): Promise<void> {
    this.ensureScope();
    const term = this.searchTerm.trim();
    const ticket = this.seq.begin(`paged:${this.scopeKey}`);
    const hadData = this.pagedProducts.length > 0;
    runInAction(() => {
      this.pagedStatus = hadData ? 'refreshing' : 'loading';
      this.pagedError = null;
    });
    try {
      const result = await productsApi.list(
        {
          status: 'active',
          is_addon: false,
          per_page: PAGE_SIZE,
          page,
          ...(term ? { search: term } : {}),
          ...(!term && typeof this.categoryFilter === 'number'
            ? { category_id: this.categoryFilter }
            : {}),
        },
        { signal: ticket.signal, salesChannel: 'pos' },
      );
      if (!ticket.isCurrent()) return;
      runInAction(() => {
        this.pagedProducts = opts.append
          ? [...this.pagedProducts, ...result.products]
          : result.products;
        this.pagedPage = result.page;
        this.pagedLastPage = result.lastPage;
        this.pagedTotal = result.total;
        this.pagedStatus = 'success';
      });
    } catch (e) {
      if (isAbortError(e) || !ticket.isCurrent()) return;
      runInAction(() => {
        this.pagedStatus = 'error';
        this.pagedError = normalizeApiError(e);
      });
    }
  }

  // ── barcode ──────────────────────────────────────────────────────────────

  /**
   * Scanner Enter path: try an exact SKU match against loaded rows first
   * (menu products, their variant rows, paged rows, addons), then fall back
   * to GET /products/barcode/{code}. Returns null when nothing matches.
   */
  findLoadedBySku(code: string): Product | null {
    const needle = code.trim().toLowerCase();
    if (!needle) return null;
    const pools = [this.menuProducts, this.pagedProducts, this.addons];
    for (const pool of pools) {
      for (const product of pool) {
        if (product.sku?.toLowerCase() === needle) return product;
        for (const variant of product.variants) {
          if (variant.status === 'active' && variant.sku?.toLowerCase() === needle) return variant;
        }
      }
    }
    return null;
  }

  /**
   * Exact id match against every loaded pool, including variant rows.
   *
   * Used by draft recovery to rebuild a persisted ticket against the LIVE
   * catalog — a restored line resolves to today's product and today's price,
   * never to a snapshot taken when the draft was written. Returns null when
   * the product is no longer sellable, which is how a withdrawn item gets
   * dropped from a recovered ticket instead of silently reappearing.
   */
  findLoadedById(productId: number): Product | null {
    const pools = [this.menuProducts, this.pagedProducts, this.addons];
    for (const pool of pools) {
      for (const product of pool) {
        if (product.id === productId) return product;
        for (const variant of product.variants) {
          if (variant.status === 'active' && variant.id === productId) return variant;
        }
      }
    }
    return null;
  }

  async lookupBarcode(code: string): Promise<Product | null> {
    this.ensureScope();
    const scope = this.scopeKey;
    const hit = await productsApi.byBarcode(code, { salesChannel: 'pos' });
    if (scope !== this.scopeKey) return null;
    return hit?.product ?? null;
  }

  /** Register lookup keeps a pack barcode's authoritative unit quantity. */
  async lookupBarcodeForSale(
    code: string,
  ): Promise<{ product: Product; unitQuantity: number } | null> {
    this.ensureScope();
    const scope = this.scopeKey;
    const hit = await productsApi.byBarcode(code, { salesChannel: 'pos' });
    if (scope !== this.scopeKey) return null;
    return hit ? { product: hit.product, unitQuantity: hit.unitQuantity } : null;
  }

  /**
   * A product by id, from the loaded pools or from the server.
   *
   * `findLoadedById` alone is right for draft recovery, where a product that is
   * not loaded is a product the cashier cannot currently sell. It is WRONG for
   * the order editor: on a large menu the store is in paged mode, and an order
   * from last Tuesday routinely contains a product that is on no loaded page.
   * Resolving only against the pools would drop those lines from the editor —
   * silently changing an order the operator only opened to look at.
   *
   * Fetched rows are cached for the life of the scope so seeding an editor with
   * a dozen lines costs at most a dozen requests once, not once per re-render.
   * Null means genuinely gone from the catalog, which the caller must surface
   * rather than skip.
   */
  async resolveProduct(productId: number): Promise<Product | null> {
    this.ensureScope();
    const scope = this.scopeKey;
    const loaded = this.findLoadedById(productId);
    if (loaded) return loaded;

    const cached = this.fetchedById.get(productId);
    if (cached !== undefined) return cached;

    const fetched = await productsApi.byId(productId, { salesChannel: 'pos' });
    if (scope !== this.scopeKey) return null;
    runInAction(() => {
      this.fetchedById.set(productId, fetched);
    });
    return fetched;
  }

  /** Resolve an addons_config product id against the loaded addon pool. */
  resolveAddon(productId: number): Product | null {
    return this.addons.find((a) => a.id === productId && a.status === 'active') ?? null;
  }

  /**
   * Variable parent for a variant row surfaced by barcode/search — needed so
   * the line inherits the parent's addons_config. Null when not loaded.
   */
  findParentOf(variant: Product): Product | null {
    if (variant.parent_id == null) return null;
    const pools = [this.menuProducts, this.pagedProducts];
    for (const pool of pools) {
      const parent = pool.find((p) => p.id === variant.parent_id);
      if (parent) return parent;
    }
    return null;
  }

  // ── scope lifecycle ──────────────────────────────────────────────────────

  /** Drop data loaded under a different organization/branch/terminal scope before any request. */
  private ensureScope(): void {
    const key = this.scopeKey;
    if (this.loadedScopeKey !== null && this.loadedScopeKey !== key) this.reset();
    this.loadedScopeKey = key;
  }

  /** Full reset — lead wires this to auth.registerScopeListener. */
  reset(): void {
    this.seq.abortAll();
    this.loadedScopeKey = null;
    this.mode = 'menu';
    this.catalogTotal = null;
    this.categories = [];
    this.menuProducts = [];
    this.addons = [];
    this.lastSync = null;
    this.status = 'idle';
    this.error = null;
    this.searchTerm = '';
    this.categoryFilter = null;
    this.pagedProducts = [];
    this.pagedPage = 1;
    this.pagedLastPage = 1;
    this.pagedTotal = 0;
    this.pagedStatus = 'idle';
    this.pagedError = null;
    // Scoped like every other cached row: a product id means something
    // different under a different organization.
    this.fetchedById.clear();
  }
}
